import type {
  CulturalSignalsDto,
  CultureContextDto,
  GenerationalInsightsDto,
} from '@inference.engine/api-client';

/**
 * Culture & Context blocks changed shape when the section was redesigned, and the generated
 * client describes both the old shape and the new one. These narrow them to one before anything
 * renders, so no component has to know which era of analysis it is looking at.
 */

/** A cultural signal: one paragraph now, a list of bullets in reports written before. */
export type RawSignal = NonNullable<CulturalSignalsDto['emerging']>;

/** A cohort: the object the redesign reads, or the bare paragraph that preceded it. */
export type RawGeneration = NonNullable<GenerationalInsightsDto['genZ']>;

/** A moment or trend: the card the redesign reads, or the bare paragraph that preceded it. */
export type RawCulturalCard = NonNullable<CultureContextDto['relevantMoments']>[number];

export const signalText = (signal?: RawSignal): string =>
  Array.isArray(signal) ? signal.join(' ') : (signal ?? '');

export const generationText = (generation?: RawGeneration): string =>
  typeof generation === 'string' ? generation : (generation?.description ?? '');

/** A cohort with every field the redesign reads, whichever era the report was written in. */
export interface NormalizedGeneration {
  description: string;
  ageRange?: string;
  expanded: string[];
  imageUrl?: string;
}

export const normalizeGeneration = (
  generation?: RawGeneration,
): NormalizedGeneration | undefined => {
  if (!generation) return undefined;
  if (typeof generation === 'string') return { description: generation, expanded: [] };
  return {
    description: generation.description,
    ageRange: generation.ageRange,
    expanded: generation.expanded,
    imageUrl: generation.imageUrl,
  };
};

export const culturalCardText = (card?: RawCulturalCard): string =>
  typeof card === 'string' ? card : [card?.title, card?.description].filter(Boolean).join(': ');
